// React & Router
// Styles
import "@/modules/styles/index.css"

// Sentry
import * as Sentry from "@sentry/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { NuqsAdapter } from "nuqs/adapters/react"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { registerSW } from "virtual:pwa-register"
import terminal from "virtual:terminal"

import { registerPreloadErrorHandler } from "@/modules/helpers/utils"
// Import the generated route tree
import { routeTree } from "@/routeTree.gen"

// Register PWA service worker - prompt mode to prevent auto-reload in WebView
const updateSW = registerSW({
  onNeedRefresh() {
    terminal.log("New version available. Will update on next manual refresh.")
    // Expose globally so pull-to-refresh or navigation can trigger it
    window.__SW_UPDATE = () => updateSW(true)
  },
  onOfflineReady() {
    terminal.log("App ready to work offline")
  }
})

// Type declaration for the global SW update function
declare global {
  interface Window {
    __SW_UPDATE?: () => void
  }
}

// Register handler for chunk load errors (auto-reload on stale chunks)
registerPreloadErrorHandler()

// Suppress Vite HMR full-reload inside React Native WebView
if (import.meta.hot && window.ReactNativeWebView) {
  import.meta.hot.on("vite:beforeFullReload", (payload: { path?: string }) => {
    if (!payload?.path) return false
  })
}

// Helper function to check if URL is localhost
const isLocalhostUrl = (url: string | undefined): boolean => {
  if (!url) return false
  return url.includes("localhost") || url.includes("127.0.0.1")
}

// Only initialize Sentry in production
if (import.meta.env.MODE === "production") {
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    release: import.meta.env.VITE_APP_VERSION || "2.0.1",
    environment: "production",

    // Performance Monitoring (for Apdex)
    tracesSampleRate: 0.1,

    // Session tracking (Crash Free Sessions & Users)
    enableLogs: true,
    sendDefaultPii: true,

    integrations: [Sentry.browserSessionIntegration(), Sentry.browserTracingIntegration()],

    // Filter out errors from localhost API calls
    beforeSend(event) {
      // Check if the error is related to a localhost API call
      const breadcrumbs = event.breadcrumbs || []
      const hasLocalhostRequest = breadcrumbs.some((breadcrumb) =>
        breadcrumb.category === "fetch" || breadcrumb.category === "xhr"
          ? isLocalhostUrl(breadcrumb.data?.url)
          : false
      )

      if (hasLocalhostRequest) {
        return null // Drop the event
      }

      return event
    },

    // Filter out transactions/traces from localhost API calls
    beforeSendTransaction(event) {
      const eventRequest = event.request
      if (isLocalhostUrl(eventRequest?.url)) {
        return null // Drop the transaction
      }
      return event
    }
  })
}

// Create a new query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
})

// Create the router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById("root")!
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <NuqsAdapter>
          <RouterProvider router={router} />
        </NuqsAdapter>
      </QueryClientProvider>
    </StrictMode>
  )
}
